import { IELTContact } from 'legacy/lut/classes/models/partner360/ELT/IELTContact'
import { IMeetingMainInfo } from 'legacy/lut/classes/models/partner360/ELT/IELTFormData'
import { IELTHPContact } from 'legacy/lut/classes/models/partner360/ELT/IELTHPContact'
import { IWWPartners } from 'legacy/lut/classes/models/partner360/IWWPartners'
import { DropdownAutocomplete } from 'legacy/lut/components/widgets/public/partner360/editors/DropdownAutocomplete'
import { AccountIds } from 'legacy/lut/components/widgets/public/partner360/ELT/ui/meetingLogistics/mainInfo/AccountIds'
import { getSeparatedPhone } from 'legacy/lut/components/widgets/public/phoneEditor/helpers/getSeparatedPhone'
import { PhoneEditor } from 'legacy/lut/components/widgets/public/phoneEditor/PhoneEditor'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { DatePicker } from 'core-components/library/datepicker'
import { Input } from 'core-components/library/inputs'

import { getFormedEltTranslation } from '../../../helpers/getFormedEltTranslation'
import { translationReplacer } from '../../../helpers/translationReplacer'
import { useUsers } from '../../../hooks/useUsers'
import { IPartnerGroupAdditionalInfo } from '../../../models/IPartnerGroupAdditionalInfo'
import { StyledInputs } from '../../styledComponents/styledInput/StyledInputs'
import { Title } from '../../styledComponents/title/Title'

import { changeDateTime } from './helpers/changeDateTime'

import styles from './MainInfo.module.scss'

interface IProps {
  formData: IMeetingMainInfo
  updateFormData: (field: string) => (value: string) => void
  partnerGroupInfo: IPartnerGroupAdditionalInfo
  partnerData: IWWPartners | null
  dataEntryId: number
}
export const MainInfo = ({
  partnerGroupInfo,
  updateFormData,
  formData,
  partnerData,
  dataEntryId,
}: IProps) => {
  const [t] = useTranslation()

  const { usersCrud, contactsCrud } = useUsers({
    partnerId: partnerData?.PartnerID,
    groupId: partnerGroupInfo.groupId,
  })

  const renderDropdownSuggestion = useCallback((item: IObjectAny) => {
    return (
      <div className={styles.renderItem}>
        <div className={styles.renderItemName}>
          {item.FirstName} {item.LastName}
        </div>
        <div className={styles.renderItemRoles}>{item.Email || '-'}</div>
        <div className={styles.renderItemRoles}>{item.Roles || '-'}</div>
      </div>
    )
  }, [])

  return (
    <div>
      <Title variant="withBackground" text={getFormedEltTranslation('meet-main-info-title')} />
      <div className={styles.root}>
        <StyledInputs label={getFormedEltTranslation('meet-location')}>
          <Input
            value={formData.MeetingLocation || '' || ''}
            onChange={updateFormData('MeetingLocation')}
          />
        </StyledInputs>
        <StyledInputs
          label={getFormedEltTranslation('meet-date-time')}
          className={styles.rowWithLimitedHeight}
        >
          <DatePicker
            minutesStep={5}
            mode={'single'}
            onChange={changeDateTime(updateFormData('MeetingDateTime'))}
            value={formData.MeetingDateTime ? new Date(formData.MeetingDateTime) : undefined}
          />
        </StyledInputs>
        <StyledInputs
          label={getFormedEltTranslation('meet-hp-contact-name-phone')}
          className={styles.rowWithLimitedHeight}
        >
          <DropdownAutocomplete<IELTHPContact>
            onChange={updateFormData('HPContactEmail')}
            placeholder={translationReplacer(getFormedEltTranslation('enter-hp-email'), t)}
            value={formData.HPContactEmail || ''}
            fieldsToSearch={['Email', 'FirstName', 'LastName']}
            calculateCaption={renderDropdownSuggestion}
            source={usersCrud}
            validateName="Email"
            mode="combobox"
            onItemChange={(item) => {
              const { phone, code } = getSeparatedPhone({
                phone: item.MainPhoneNorm || item.MobilePhoneNorm || '',
              })
              updateFormData('HPContactPhone')(phone)
              updateFormData('HPContactPhoneCode')(code)
            }}
            translation="jbp-hpcontactinfo-email-required"
          />
          <PhoneEditor
            codeValue={formData.HPContactPhoneCode}
            phoneValue={formData.HPContactPhone}
            label={'phone'}
            onCodeChange={updateFormData('HPContactPhoneCode')}
            onPhoneChange={updateFormData('HPContactPhone')}
          />
        </StyledInputs>
        <StyledInputs
          label={getFormedEltTranslation('meet-company-contact-name-phone')}
          className={styles.rowWithLimitedHeight}
        >
          <DropdownAutocomplete<IELTContact>
            onChange={updateFormData('CompanyContactEmail')}
            placeholder={translationReplacer(getFormedEltTranslation('enter-account-email'), t)}
            value={formData.CompanyContactEmail || ''}
            fieldsToSearch={['Email', 'FirstName', 'LastName']}
            calculateCaption={renderDropdownSuggestion}
            source={contactsCrud}
            validateName="Id"
            mode="combobox"
            onItemChange={(item) => {
              const { phone, code } = getSeparatedPhone({
                phone: item.MainPhoneNorm || item.WorkPhoneNorm || item.MobilePhoneNorm || '',
              })
              updateFormData('CompanyContactWorkPhonePrefix')(code)
              updateFormData('CompanyContactWorkPhone')(phone)
            }}
            translation="jbp-hpcontactinfo-email-required"
          />
          <PhoneEditor
            codeValue={formData.CompanyContactWorkPhonePrefix}
            phoneValue={formData.CompanyContactWorkPhone}
            label={'phone'}
            onCodeChange={updateFormData('CompanyContactWorkPhonePrefix')}
            onPhoneChange={updateFormData('CompanyContactWorkPhone')}
          />
        </StyledInputs>
        <StyledInputs
          label={getFormedEltTranslation('meet-company-admin-name-phone')}
          className={styles.rowWithLimitedHeight}
        >
          <DropdownAutocomplete<IELTContact>
            onChange={updateFormData('CompanyAdminEmail')}
            placeholder={translationReplacer(
              getFormedEltTranslation('enter-account-admin-email'),
              t
            )}
            value={formData.CompanyAdminEmail || ''}
            fieldsToSearch={['Email', 'FirstName', 'LastName']}
            calculateCaption={renderDropdownSuggestion}
            source={contactsCrud}
            validateName="Id"
            mode="combobox"
            onItemChange={(item) => {
              const { phone, code } = getSeparatedPhone({
                phone: item.MainPhoneNorm || item.WorkPhoneNorm || item.MobilePhoneNorm || '',
              })
              updateFormData('CompanyAdminWorkPhonePrefix')(code)
              updateFormData('CompanyAdminWorkPhone')(phone)
            }}
            translation="jbp-hpcontactinfo-email-required"
          />
          <PhoneEditor
            codeValue={formData.CompanyAdminWorkPhonePrefix}
            phoneValue={formData.CompanyAdminWorkPhone}
            label={'phone'}
            onCodeChange={updateFormData('CompanyAdminWorkPhonePrefix')}
            onPhoneChange={updateFormData('CompanyAdminWorkPhone')}
          />
        </StyledInputs>
        <StyledInputs label={getFormedEltTranslation('-meet-hp-executive-sponsor')}>
          <Input
            value={formData.HpExecutiveSponsor || ''}
            onChange={updateFormData('HpExecutiveSponsor')}
          />
        </StyledInputs>
        <div className={styles.segments}>
          <StyledInputs label={getFormedEltTranslation('meet-account-segment')}>
            <Input
              value={formData.AccountSegment || ''}
              onChange={updateFormData('AccountSegment')}
            />
          </StyledInputs>
        </div>
        <AccountIds
          partnerGroupInfo={partnerGroupInfo}
          partnerData={partnerData}
          dataEntryId={dataEntryId}
        />
      </div>
    </div>
  )
}
